import React, { useEffect, useRef, useState } from "react";
import { api, i18n, MODULES } from "@/lib/api";
import ResultsPane from "./ResultsPane";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result.toString();
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function humanFileSize(numBytes) {
  if (!numBytes && numBytes !== 0) return "";
  if (numBytes < 1024) return `${numBytes} B`;
  if (numBytes < 1024 * 1024) return `${(numBytes / 1024).toFixed(1)} KB`;
  return `${(numBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Generator({ language, profile, syllabi, onGenerated, locked }) {
  const t = i18n[language];
  const [moduleId, setModuleId] = useState(1);
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState("");
  const [syllabusId, setSyllabusId] = useState("");
  const [media, setMedia] = useState([]);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const [doc, setDoc] = useState(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const creditCost = media.length === 0 ? 1 : media.some((m) => m.mime_type.startsWith("audio") || m.mime_type.startsWith("video")) ? 3 : 2;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const out = [];
    for (const f of files) {
      const b64 = await fileToBase64(f);
      out.push({
        mime_type: f.type || "application/octet-stream",
        data_base64: b64,
        filename: f.name,
        size: f.size,
      });
    }
    setMedia(out);
    // Reset the file input so the same file can be re-selected after removal
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllAttachments = () => {
    setMedia([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        const buf = await blob.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = "";
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        const b64 = btoa(bin);
        setMedia([{ mime_type: blob.type, data_base64: b64, filename: `voice-${Date.now()}.webm`, size: bytes.length }]);
        stream.getTracks().forEach((track) => track.stop());
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
    } catch (e) {
      setError(language === "es" ? "Microfono no disponible." : "Microphone unavailable.");
    }
  };

  const stopRecording = () => {
    try {
      mediaRecRef.current?.stop();
    } catch (e) {
      /* noop */
    }
    setRecording(false);
  };

  const pollUntilComplete = (documentId, language) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const start = Date.now();
    const MAX_MS = 4 * 60 * 1000; // 4 minutes hard ceiling (matches backend watchdog at 240s)
    const tick = async () => {
      try {
        const elapsed = Math.round((Date.now() - start) / 1000);
        if (Date.now() - start > MAX_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          setError(
            language === "es"
              ? "El procesamiento esta tardando mas de 4 minutos. El documento aparecera en la Biblioteca cuando termine."
              : "Processing is taking longer than 4 minutes. The document will appear in the Vault when ready.",
          );
          return;
        }
        const r = await api.getDocument(documentId);
        const updated = r.document;
        const status = updated?.citations_json?.status;
        if (status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setDoc(updated);
          setBusy(false);
          setStatusText("");
          onGenerated?.({ document: updated });
        } else if (status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          setStatusText("");
          setError(
            (language === "es" ? "Generacion fallida: " : "Generation failed: ") +
              (updated?.citations_json?.error || "Unknown error"),
          );
        } else {
          // Still processing
          const progressNote = updated?.citations_json?.progress || "";
          setStatusText(
            (language === "es"
              ? "La IA esta pensando "
              : "AI is thinking ") +
              `(${elapsed}s${progressNote ? " - " + progressNote : ""})`,
          );
        }
      } catch (e) {
        // Non-fatal: just continue polling
      }
    };
    // Immediate tick + every 3 seconds
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  const submit = async () => {
    setError("");
    if (!prompt.trim() && media.length === 0) {
      setError(language === "es" ? "Escribe una instruccion o adjunta un archivo." : "Type a prompt or attach a file.");
      return;
    }
    setBusy(true);
    setDoc(null);
    setStatusText(language === "es" ? "Enviando peticion" : "Submitting request");
    try {
      const result = await api.generate({
        module_number: moduleId,
        prompt: prompt || "(media-only input)",
        language,
        duration_minutes: moduleId === 2 ? parseInt(duration || "0", 10) || null : null,
        syllabus_id: syllabusId || null,
        media: media.length ? media : null,
      });
      const docId = result?.document?.document_id;
      if (!docId) {
        throw new Error("No document_id returned");
      }
      setMedia([]);
      pollUntilComplete(docId, language);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Generation failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setBusy(false);
      setStatusText("");
    }
  };

  const cancelPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setBusy(false);
    setStatusText("");
  };

  const handleFeedback = async (rating) => {
    if (!doc) return;
    let issue = null;
    if (rating === "report") {
      issue = window.prompt(language === "es" ? "Describe el problema:" : "Describe the issue:") || "";
      if (!issue) return;
    }
    try {
      await api.submitFeedback({ document_id: doc.document_id, rating, issue_text: issue });
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div data-testid="generator-pane">
      <div style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "0.5rem", display: "flex", gap: "0.5rem" }} data-testid="module-selector">
        {MODULES.map((m) => (
          <button
            key={m.id}
            data-testid={`module-chip-${m.id}`}
            className={`module-chip ${moduleId === m.id ? "active" : ""}`}
            disabled={locked}
            onClick={() => setModuleId(m.id)}
          >
            {m.id}. {m[language]}
          </button>
        ))}
      </div>

      <div className="line" />

      {locked && (
        <div data-testid="locked-banner" className="txt-mute" style={{ marginBottom: "1rem", padding: "0.75rem 0", borderTop: "1px dashed var(--ink-faint)", borderBottom: "1px dashed var(--ink-faint)" }}>
          {t.subscriptionInactive}
        </div>
      )}

      <div className="input-line focus-line" style={{ marginBottom: "1rem" }}>
        <label className="h-eyebrow" htmlFor="prompt-input">
          {language === "es" ? "Entrada Unificada" : "Unified Input"}
        </label>
        <textarea
          id="prompt-input"
          data-testid="prompt-input"
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={language === "es" ? "Escribe, dicta o pega tu instruccion..." : "Type, dictate, or paste your instruction..."}
          disabled={locked || busy}
        />
      </div>

      {moduleId === 2 && (
        <div className="input-line focus-line" style={{ marginBottom: "1rem", maxWidth: 280 }}>
          <label className="h-eyebrow" htmlFor="duration-input">
            {t.durationMinutes}
          </label>
          <input
            id="duration-input"
            data-testid="duration-input"
            type="number"
            min="1"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="75"
            disabled={locked || busy}
          />
        </div>
      )}

      {syllabi.length > 0 && (
        <div className="input-line focus-line" style={{ marginBottom: "1rem", maxWidth: 480 }}>
          <label className="h-eyebrow" htmlFor="syllabus-select">
            {t.syllabus}
          </label>
          <select
            id="syllabus-select"
            data-testid="syllabus-select"
            value={syllabusId}
            onChange={(e) => setSyllabusId(e.target.value)}
            disabled={locked || busy}
            style={{ background: "var(--bg)" }}
          >
            <option value="">{language === "es" ? "(ninguno)" : "(none)"}</option>
            {syllabi.map((s) => (
              <option key={s.syllabus_id} value={s.syllabus_id}>
                {s.course_title} {s.semester_label ? `- ${s.semester_label}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Attachment list with per-item remove */}
      {media.length > 0 && (
        <div data-testid="attachment-list" style={{ marginBottom: "1rem" }}>
          <div className="h-eyebrow" style={{ marginBottom: "0.4rem" }}>
            {language === "es" ? "Archivos adjuntos" : "Attached files"}
          </div>
          {media.map((m, i) => (
            <div
              key={i}
              data-testid={`attachment-row-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.55rem 0",
                borderBottom: "1px solid var(--line)",
                gap: "1rem",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: "0.94rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.filename || `attachment-${i + 1}`}
                </div>
                <div className="kbd">
                  {m.mime_type} {m.size ? ` - ${humanFileSize(m.size)}` : ""}
                </div>
              </div>
              <button
                data-testid={`attachment-remove-${i}`}
                className="btn-link"
                onClick={() => removeAttachment(i)}
                disabled={busy}
                style={{ color: "var(--danger)" }}
              >
                {language === "es" ? "Quitar" : "Remove"}
              </button>
            </div>
          ))}
          {media.length > 1 && (
            <button
              data-testid="attachment-clear-all"
              className="btn-link"
              onClick={clearAllAttachments}
              disabled={busy}
              style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}
            >
              {language === "es" ? "Quitar todo" : "Clear all"}
            </button>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,audio/*,video/*"
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
          data-testid="file-input"
        />
        <button
          data-testid="attach-file-btn"
          className="btn-ghost"
          disabled={locked || busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {language === "es" ? "Adjuntar archivo" : "Attach file"}
        </button>
        {!recording ? (
          <button
            data-testid="mic-start"
            className="btn-ghost"
            disabled={locked || busy}
            onClick={startRecording}
          >
            {language === "es" ? "Grabar voz" : "Record voice"}
          </button>
        ) : (
          <button
            data-testid="mic-stop"
            className="btn-ghost"
            onClick={stopRecording}
          >
            {language === "es" ? "Detener" : "Stop"}
          </button>
        )}
        <span className="kbd" style={{ marginLeft: "auto" }} data-testid="credit-cost-display">
          {t.creditCost}: {creditCost}
        </span>
      </div>

      <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <button
          data-testid="generate-btn"
          className="btn-primary"
          disabled={locked || busy}
          onClick={submit}
        >
          {busy ? "..." : t.submit}
        </button>
        {busy && (
          <>
            <span data-testid="processing-status" className="txt-mute" style={{ fontSize: "0.9rem" }}>
              {statusText || t.submitting}
            </span>
            <button
              data-testid="cancel-poll-btn"
              className="btn-link"
              onClick={cancelPolling}
              style={{ fontSize: "0.85rem", color: "var(--ink-mute)" }}
            >
              {language === "es" ? "Cancelar espera" : "Stop waiting"}
            </button>
          </>
        )}
        {error && (
          <span data-testid="generator-error" className="txt-danger" style={{ fontSize: "0.9rem" }}>
            {error}
          </span>
        )}
      </div>

      {doc && <ResultsPane document={doc} language={language} onFeedback={handleFeedback} />}
    </div>
  );
}
